/**
 * スプレッドシート同期マネージャー
 * Googleスプレッドシートとリアルタイム同期する機能を提供
 */

import axios from 'axios'
import Papa from 'papaparse'
import proj4 from 'proj4'

const MAX_ROWS = 1000
const DEFAULT_INTERVAL = 30 // 秒

// ソースとレイヤーのID
export const SYNC_SOURCE_ID = 'spreadsheet-sync-source'
export const SYNC_LAYER_POINT = 'spreadsheet-sync-point'
export const SYNC_LAYER_LABELS = 'spreadsheet-sync-labels'

class SpreadsheetSyncManager {
  constructor() {
    this.config = null
    this.map = null
    this.intervalId = null
    this.lastDataHash = null
    this.isRunning = false
    this.onUpdateCallback = null
    this.onErrorCallback = null
  }

  /**
   * 初期化
   */
  init(map) {
    this.map = map
    this.loadConfig()

    // アイドル時にレイヤーを最上位に移動（他レイヤー追加後に実行される）
    if (this.map && !this._idleHandlerSet) {
      this._idleHandlerSet = true
      this.map.on('idle', () => {
        this.bringLayersToTop()
      })
    }

    // 保存された設定があり、自動同期ONなら開始
    if (this.config && this.config.autoSync) {
      this.startSync()
    }
  }

  /**
   * レイヤーを最上位に移動
   */
  bringLayersToTop() {
    if (!this.map) return
    if (this.map.getLayer(SYNC_LAYER_POINT)) {
      this.map.moveLayer(SYNC_LAYER_POINT)
    }
    if (this.map.getLayer(SYNC_LAYER_LABELS)) {
      this.map.moveLayer(SYNC_LAYER_LABELS)
    }
  }

  /**
   * 設定を取得（メモリのみ、ローカルストレージは使わない）
   */
  loadConfig() {
    return this.config
  }

  /**
   * 設定を保存（メモリのみ）
   */
  saveConfig(config) {
    this.config = config
  }

  /**
   * 設定をクリア
   */
  clearConfig() {
    this.config = null
    this.lastDataHash = null
  }

  /**
   * URLからスプレッドシートIDを抽出
   */
  extractSheetId(url) {
    const match = url.match(/\/spreadsheets\/d\/([a-zA-Z0-9-_]+)/)
    return match ? match[1] : null
  }

  /**
   * スプレッドシートからデータを取得
   */
  async fetchData(url) {
    const sheetId = this.extractSheetId(url)
    if (!sheetId) {
      throw new Error('無効なスプレッドシートURLです')
    }

    const csvUrl = `https://docs.google.com/spreadsheets/d/${sheetId}/export?format=csv`
    const response = await axios.get(csvUrl, { timeout: 15000 })

    const parsed = Papa.parse(response.data, {
      header: true,
      skipEmptyLines: true,
      dynamicTyping: true
    })

    if (!parsed.data || parsed.data.length === 0) {
      throw new Error('データが取得できませんでした。公開設定を確認してください。')
    }

    return {
      data: parsed.data,
      columns: parsed.meta.fields || []
    }
  }

  /**
   * データのハッシュを計算（差分検出用）
   */
  computeHash(data) {
    const str = JSON.stringify(data)
    let hash = 0
    for (let i = 0; i < str.length; i++) {
      const char = str.charCodeAt(i)
      hash = ((hash << 5) - hash) + char
      hash = hash & hash
    }
    return hash.toString(16)
  }

  /**
   * 座標列を自動検出
   */
  detectCoordinateColumns(columns) {
    const latKeywords = ['lat', 'latitude', '緯度', 'y', 'y座標', 'north', 'northing', '北緯']
    const lngKeywords = ['lng', 'lon', 'longitude', '経度', 'x', 'x座標', 'east', 'easting', '東経']
    const labelKeywords = ['name', '名前', '名称', 'label', 'title', '地名', '施設名']

    const latCol = columns.find(col =>
      latKeywords.some(kw => col.toLowerCase().includes(kw.toLowerCase()))
    )
    const lngCol = columns.find(col =>
      lngKeywords.some(kw => col.toLowerCase().includes(kw.toLowerCase()))
    )
    const labelCol = columns.find(col =>
      labelKeywords.some(kw => col.toLowerCase().includes(kw.toLowerCase()))
    )

    return { latCol, lngCol, labelCol }
  }

  /**
   * データをGeoJSONに変換
   * selectedCRS: 'WGS84' | '公共座標N系'
   */
  toGeoJSON(data, config) {
    const features = []
    const { latColumn, lngColumn, labelColumn, selectedCRS } = config

    // 座標系がWGS84かどうか
    const isWGS84 = !selectedCRS || selectedCRS === 'WGS84'

    // 公共座標系の場合、EPSGコードを算出（1系=2443, 2系=2444, ...）
    let epsgCode = null
    if (!isWGS84) {
      const keiNum = parseInt(selectedCRS.replace(/[^0-9]/g, ''))
      if (keiNum >= 1 && keiNum <= 19) {
        epsgCode = `EPSG:${2442 + keiNum}` // EPSG:2443 = 1系
      }
    }

    for (const row of data.slice(0, MAX_ROWS)) {
      // latColumn = X列, lngColumn = Y列
      const xStr = row[latColumn]
      const yStr = row[lngColumn]
      if (!xStr && xStr !== 0) continue
      if (!yStr && yStr !== 0) continue

      // 数値変換（カンマ区切り対応）
      const x = parseFloat(String(xStr).replace(/,/g, ''))
      const y = parseFloat(String(yStr).replace(/,/g, ''))

      if (isNaN(x) || isNaN(y)) continue

      let lat, lng
      try {
        if (isWGS84) {
          // WGS84: X=経度, Y=緯度
          lng = x
          lat = y
        } else if (epsgCode) {
          // 公共座標系: proj4で変換
          // X=東距, Y=北距 → proj4は [easting, northing] = [x, y] で受け取る
          // ただしスプレッドシートの慣習で X=東距, Y=北距 なので [x, y] の順
          ;[lng, lat] = proj4(epsgCode, 'EPSG:4326', [x, y])
        } else {
          continue
        }
      } catch (e) {
        console.warn('[SpreadsheetSync] 座標変換エラー:', e, { x, y, crs: selectedCRS })
        continue
      }

      if (isNaN(lat) || isNaN(lng)) continue
      if (lat < -90 || lat > 90 || lng < -180 || lng > 180) continue

      const properties = { ...row }
      if (labelColumn && row[labelColumn]) {
        properties._label = row[labelColumn]
      }

      features.push({
        type: 'Feature',
        geometry: {
          type: 'Point',
          coordinates: [lng, lat]
        },
        properties
      })
    }

    return {
      type: 'FeatureCollection',
      features
    }
  }

  /**
   * 地図にソースとレイヤーを追加
   */
  ensureLayersAdded() {
    if (!this.map) return

    // ソースを追加
    if (!this.map.getSource(SYNC_SOURCE_ID)) {
      this.map.addSource(SYNC_SOURCE_ID, {
        type: 'geojson',
        data: { type: 'FeatureCollection', features: [] }
      })
    }

    // ポイントレイヤー
    if (!this.map.getLayer(SYNC_LAYER_POINT)) {
      this.map.addLayer({
        id: SYNC_LAYER_POINT,
        type: 'circle',
        source: SYNC_SOURCE_ID,
        paint: {
          'circle-radius': 12,
          'circle-color': '#ff4444',
          'circle-stroke-width': 3,
          'circle-stroke-color': '#ffffff',
          'circle-opacity': 0.9
        }
      })
    } else {
      // すでに存在する場合は最上位に移動
      this.map.moveLayer(SYNC_LAYER_POINT)
    }

    // ラベルレイヤー
    if (!this.map.getLayer(SYNC_LAYER_LABELS)) {
      this.map.addLayer({
        id: SYNC_LAYER_LABELS,
        type: 'symbol',
        source: SYNC_SOURCE_ID,
        layout: {
          'text-field': ['get', '_label'],
          'text-size': 16,
          'text-font': ['Noto Sans CJK JP Bold', 'Arial Unicode MS Bold'],
          'text-offset': [0, 1.8],
          'text-anchor': 'top',
          'text-optional': true
        },
        paint: {
          'text-color': '#222',
          'text-halo-color': '#fff',
          'text-halo-width': 2
        }
      })
    } else {
      // すでに存在する場合は最上位に移動
      this.map.moveLayer(SYNC_LAYER_LABELS)
    }
  }

  /**
   * 地図を更新
   */
  updateMap(geojson, fitBounds = false) {
    if (!this.map) return

    this.ensureLayersAdded()

    const source = this.map.getSource(SYNC_SOURCE_ID)
    if (source) {
      source.setData(geojson)
    }

    // フィット処理
    if (fitBounds && geojson.features.length > 0) {
      this.fitToFeatures(geojson)
    }
  }

  /**
   * フィーチャーの範囲にフィット
   */
  fitToFeatures(geojson) {
    if (!this.map || !geojson.features || geojson.features.length === 0) return

    // バウンディングボックスを計算
    let minLng = Infinity, minLat = Infinity
    let maxLng = -Infinity, maxLat = -Infinity

    for (const feature of geojson.features) {
      if (feature.geometry && feature.geometry.type === 'Point') {
        const [lng, lat] = feature.geometry.coordinates
        if (lng < minLng) minLng = lng
        if (lng > maxLng) maxLng = lng
        if (lat < minLat) minLat = lat
        if (lat > maxLat) maxLat = lat
      }
    }

    if (minLng === Infinity) return

    // フィット実行
    this.map.fitBounds(
      [[minLng, minLat], [maxLng, maxLat]],
      { padding: 50, maxZoom: 16 }
    )
  }

  /**
   * 地図からデータをクリア
   */
  clearMap() {
    if (!this.map) return

    const source = this.map.getSource(SYNC_SOURCE_ID)
    if (source) {
      source.setData({ type: 'FeatureCollection', features: [] })
    }
  }

  /**
   * 同期を実行（1回）
   * @param {boolean} fitOnChange - 変更時にフィットするか（初回true）
   */
  async syncOnce(fitOnChange = false) {
    if (!this.config || !this.config.url) {
      throw new Error('同期設定がありません')
    }

    const { data } = await this.fetchData(this.config.url)
    const newHash = this.computeHash(data)

    // 差分があれば更新
    const hasChanged = newHash !== this.lastDataHash
    if (hasChanged) {
      const geojson = this.toGeoJSON(data, this.config)
      this.updateMap(geojson, fitOnChange)
      this.lastDataHash = newHash

      // コールバック
      if (this.onUpdateCallback) {
        this.onUpdateCallback({
          count: geojson.features.length,
          total: data.length,
          truncated: data.length > MAX_ROWS
        })
      }
    }

    return { hasChanged, count: data.length }
  }

  /**
   * 定期同期を開始
   * @param {boolean} fitOnFirstSync - 初回同期時にフィットするか
   */
  startSync(fitOnFirstSync = true) {
    if (this.isRunning) return
    if (!this.config) return

    this.isRunning = true
    const interval = (this.config.interval || DEFAULT_INTERVAL) * 1000

    // 初回実行（フィットあり）
    this.syncOnce(fitOnFirstSync).catch(err => {
      console.error('[SpreadsheetSync] 同期エラー:', err)
      if (this.onErrorCallback) {
        this.onErrorCallback(err.message)
      }
    })

    // 定期実行（フィットなし）
    this.intervalId = setInterval(async () => {
      try {
        await this.syncOnce(false)
      } catch (err) {
        console.error('[SpreadsheetSync] 同期エラー:', err)
        if (this.onErrorCallback) {
          this.onErrorCallback(err.message)
        }
      }
    }, interval)

  }

  /**
   * 定期同期を停止
   */
  stopSync() {
    if (this.intervalId) {
      clearInterval(this.intervalId)
      this.intervalId = null
    }
    this.isRunning = false
  }

  /**
   * コールバック設定
   */
  onUpdate(callback) {
    this.onUpdateCallback = callback
  }

  onError(callback) {
    this.onErrorCallback = callback
  }

}

// シングルトンインスタンス
export const spreadsheetSyncManager = new SpreadsheetSyncManager()
export default spreadsheetSyncManager
